import {
  DTCompoundConditionNode,
  CompoundConditionNodeOutputData,
  CompoundConditionNodeRule,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {OutputType} from 'common/src/modules/nodes/nodes.types';
import {
  formatFallbackOutputLine,
  formatOutputHeader,
  formatShortOutputLine,
  getBaseHeader,
  getDestinationPhrase,
  sortOutputsByType,
} from './utils';
import {
  ConditionOperatorKey,
  ConditionOperatorKeyToText,
} from 'common/src/modules/conditions/conditions.types';

function formatCompoundConditionRule(rule: CompoundConditionNodeRule): string {
  const signal = rule.signal?.value ?? 'unknown';
  const operator = ConditionOperatorKeyToText[rule.ruleOperator as ConditionOperatorKey];

  const ruleValue =
    rule.ruleValue?.textValues?.join(', ') ??
    rule.ruleValue?.numericValues?.join(', ') ??
    (rule.ruleValue?.booleanValue !== null && rule.ruleValue.booleanValue) ??
    rule.ruleValue?.dateValues?.join(', ') ??
    (rule.ruleValue?.datesRange?.from &&
      rule.ruleValue?.datesRange?.to &&
      `${rule.ruleValue?.datesRange?.from} - ${rule.ruleValue?.datesRange?.to}`) ??
    'any value';

  return `${signal} ${operator} ${ruleValue}`;
}

function formatCompoundConditionOutputLine(
  output: CompoundConditionNodeOutputData,
  outputIndex: number,
  includeValues: boolean,
  fallbackCreativeNodeId?: number,
): string {
  const isFallback = output.type === OutputType.Fallback;
  const outputHeader = formatOutputHeader(outputIndex, output);

  if (!includeValues) {
    return formatShortOutputLine(output, fallbackCreativeNodeId);
  }

  if (isFallback) {
    return formatFallbackOutputLine(
      output,
      fallbackCreativeNodeId,
      'when none of the compound conditions above are met',
    );
  }

  const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);

  if (output.sections.length === 0) {
    return `${outputHeader} ${dest}.`;
  }

  const sectionLines = output.sections
    .map((section, sectionIdx) => {
      const ruleTexts = section.rules
        .map((rule) => {
          const ruleText = formatCompoundConditionRule(rule);

          return `  - ${ruleText}`;
        })
        .join('\n');

      return `When Section ${sectionIdx + 1}:\n${ruleTexts}`;
    })
    .join('\n');

  return `${outputHeader} ${dest}\n${sectionLines}.`;
}

export function compoundConditionCardToText(
  card: DTCompoundConditionNode,
  mode: 'short' | 'full',
  fallbackCreativeNodeId?: number,
): string {
  const header = getBaseHeader(card);
  const purpose =
    mode === 'short'
      ? 'Purpose: Choose output by compound condition (multiple signal rules)'
      : null;

  const sortedOutputs = sortOutputsByType(card.outputs);
  const outputLines = sortedOutputs.map((output, index) =>
    formatCompoundConditionOutputLine(output, index, mode === 'full', fallbackCreativeNodeId),
  );

  const parts = [header, purpose, ...outputLines];

  return parts.filter(Boolean).join('\n');
}
